import { ExpandMore } from "@mui/icons-material";
import { Button, TextField } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { getIn, useField, useFormikContext } from "formik"; // Importar `useField` para acceder a `meta`
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyledAutocomplete } from "../../styled/formik";
import { buildArtFieldLabel } from "./artFieldLabelUtils";
import { useArtFieldMetadata } from "./artFieldMetadataContext";
import FormikAutocompleteNoOptionsPopper from "./formikAutocompleteNoOptionsPopper";

/**
 * En este componente existen 3 estados a la vez. 
 * 1) value(useState): El valor del autocomplete que contiene el objeto seleccionado.
 * 2) values[props.name](useFormikContext): El valor de Formik
 * 3) inputValue: El valor del Input que puede ser distinto al de formik ya que puede cambiar y no cambiar la opcion seleccionada. 
 */
const DEFAULT_OPTIONS_LIMIT = 50;

const FormikAutocomplete = ({ options, ...props }) => {
  const inputRef = useRef(null);
  const { setFieldValue, values } = useFormikContext();
  const [field, meta] = useField(props.name); // Usar `useField` para obtener `meta`
  const formikValue = field.value ?? getIn(values, props.name);
  const fieldMetadata = useArtFieldMetadata(props.name);
  const label = buildArtFieldLabel(props.label, fieldMetadata);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_OPTIONS_LIMIT);
  const loading = open && options.length === 0 && !!props.getOptions;
  const getOptionLabel = useCallback((option) => {
    if (typeof option === "string") {
      return option;
    }
    const label = props.labelOption
      ? props.labelOption(option)
      : option.nombre;
    return props.formatLabelOption ? props.formatLabelOption(label) : label;
  }, [props.labelOption, props.formatLabelOption]);
  const getValue = useCallback((v) => {
    const optionFound = options.find((o) => {
      if (props.returnProp) {
        return o[props.returnProp] === formikValue;
      } else {
        return o.id === formikValue;
      }
    });
    if (formikValue === null || formikValue === undefined || (formikValue === "" && !optionFound)) {
      return null;
    }
    return optionFound ? optionFound : v;
  }, [options, props.returnProp, formikValue]);

  const initialSelected = getValue(null);
  const [value, setValue] = useState(initialSelected);
  const [inputValue, setInputValue] = useState(
    initialSelected ? getOptionLabel(initialSelected) : "",
  );
  useEffect(() => {
    const nextValue = getValue(null);
    setValue(nextValue);
    if (nextValue) {
      setInputValue(getOptionLabel(nextValue));
    } else if (!formikValue) {
      setInputValue("");
    }
  }, [formikValue, options, getValue, getOptionLabel]);

  useEffect(() => {
    if (loading) {
      props.getOptions();
    }
  }, [loading]);

  const onInputChange = (nextValue, reason) => {
    if (reason === "reset") {
      return;
    }
    if (limit !== DEFAULT_OPTIONS_LIMIT) {
      setLimit(DEFAULT_OPTIONS_LIMIT);
    }
    if (nextValue !== inputValue) {
      setInputValue(nextValue);
    }
    if (reason === "clear" && nextValue === "") {
      setFieldValue(props.name, null);
      setValue(null);
    } else if (props.freeSolo && nextValue !== formikValue) {
      setFieldValue(props.name, nextValue);
    }
  };

  const onChange = (nextValue) => {
    setFieldValue(
      props.name,
      nextValue ? (props.returnProp ? nextValue[props.returnProp] : nextValue.id) : nextValue,
    );
    const nextInput = nextValue ? getOptionLabel(nextValue) : "";
    if (nextInput !== inputValue) {
      setInputValue(nextInput);
    }
    if (props.callBack) {
      props.callBack(nextValue);
    }
  };

  const valueIsNotInOptions = () => {
    return (
      props.helperText &&
      formikValue &&
      (props.optionId
        ? !options.some((o) => o[props.optionId] === formikValue)
        : !options.some((o) => o.nombre === formikValue))
    );
  };

  const memorizedFilterOptions = useCallback(
    createFilterOptions({
      stringify: props.filterOptions ? (option) => props.filterOptions(option) : undefined,
    }),
    [props.filterOptions, limit]
  );
  const filteredTotalCount = useMemo(() => {
    if (props.showAllOptions || options.length < DEFAULT_OPTIONS_LIMIT) {
      return 0;
    }
    if (inputValue.length < 3) return 0;
    return memorizedFilterOptions(options, { inputValue, getOptionLabel }).length;
  }, [options, inputValue, memorizedFilterOptions, props.showAllOptions, getOptionLabel]);
  const filterOptions = useCallback((autocompleteOptions, state) => {
    if (props.showAllOptions || autocompleteOptions.length < DEFAULT_OPTIONS_LIMIT) {
      // Para autocompletes de pocos valores deberiamos limitar a 3 caracters, ejemplo: filtros de tablas.
      return memorizedFilterOptions(autocompleteOptions, state);
    }
    if (inputValue.length < 3) return [];
    const filteredOptions = memorizedFilterOptions(autocompleteOptions, { ...state, inputValue });
    return filteredOptions.slice(0, limit);
  }, [inputValue, memorizedFilterOptions, props.showAllOptions, limit]);

  const noOptionsText = props.showAllOptions || options.length < DEFAULT_OPTIONS_LIMIT || inputValue.length >= 3
    ? "No se encontraron opciones"
    : "Ingrese al menos 3 caracteres para ver opciones";

  return (
    <>
      <Autocomplete
        openOnFocus={true}
        open={open}
        options={options}
        noOptionsText={noOptionsText}
        freeSolo={props.freeSolo}
        loading={loading}
        groupBy={() => ""}
        sx={props.sx}
        fullWidth
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        value={value}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        renderOption={(autoCompleteProps, option) => (
          <li {...autoCompleteProps} key={option.id ?? (props.renderOption ? props.renderOption(option) : option.nombre)}>
            {props.renderOption ? props.renderOption(option) : option.nombre}
          </li>
        )}
        inputValue={inputValue}
        onChange={(event, value) => onChange(value)}
        onInputChange={(event, value, reason) => onInputChange(value, reason)}
        disabled={props.disabled}
        renderInput={(params) => {
          return (
            <>
              <TextField
                {...params}
                name={props.name}
                label={label}
                inputRef={inputRef}
                variant={props.variant ? props.variant : "outlined"}
                type={props.type}
                autoComplete={props.autoComplete}
                helperText={
                  meta.touched && meta.error
                    ? meta.error
                    : valueIsNotInOptions()
                      ? props.helperText
                      : null
                } // Mostrar mensaje de error si hay validación
                error={meta.touched && meta.error ? true : false} // Error basado en Formik
                FormHelperTextProps={{
                  sx: { color: "red" }, // Puedes personalizar el color del texto de ayuda
                }}
                sx={
                  {
                    ...(params.sx ? params.sx : {}),
                    width: "100%",
                    verticalAlign: "initial",
                  }
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
              {props.freeSolo && inputValue.length < 3 && options.length > DEFAULT_OPTIONS_LIMIT && open &&
                <FormikAutocompleteNoOptionsPopper noOptionsText={noOptionsText} anchorEl={inputRef?.current} />
              }

            </>
          );
        }}
        renderGroup={(params) => (
          <div key={params.key}>
            {params.children}
            {limit < filteredTotalCount && (
              <StyledAutocomplete.ShowMoreButtonContainer>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLimit((prev) => prev + DEFAULT_OPTIONS_LIMIT);
                  }}
                >
                  <ExpandMore />
                </Button>
              </StyledAutocomplete.ShowMoreButtonContainer>
            )}
          </div>
        )}
      />

    </>
  );
};

export default FormikAutocomplete;
